import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import type { GuestQuestionType } from "@shared/modules/basket/types/common/serializer-types";

const STANDARD_INFO_FIELDS = new Set([
  "collect_info_first_name",
  "collect_info_last_name",
  "collect_info_email",
  "collect_info_phone_number",
]);

interface ExpressCheckoutEligibilityOptions {
  itemGroups: TicketItemGroupType[];
  basketCustomQuestions?: GuestQuestionType[];
  bypassCustomQuestions?: boolean;
  isWaitlist?: boolean;
}

/**
 * Checks whether all item groups in a basket qualify for express checkout
 * (Apple Pay / Google Pay). Disqualifying conditions:
 *  - basket is a waitlist order
 *  - basket-level custom questions are present (unless bypassed by waffle flag)
 *  - shipping type requires physical delivery
 *  - item-group-level custom questions are present (unless bypassed by waffle flag)
 *  - non-standard info fields are collected
 *  - per-ticket info collection is required
 */
export function isEligibleForExpressCheckout({
  itemGroups,
  basketCustomQuestions = [],
  bypassCustomQuestions = false,
  isWaitlist = false,
}: ExpressCheckoutEligibilityOptions): boolean {
  if (itemGroups.length === 0) return false;

  if (isWaitlist) return false;

  if (!bypassCustomQuestions && basketCustomQuestions.length > 0) return false;

  return itemGroups.every((group) => {
    if (!bypassCustomQuestions) {
      const hasCustomQuestions =
        group.info_custom_question_data &&
        group.info_custom_question_data.length > 0;
      if (hasCustomQuestions) return false;
    }

    if (group.collect_info_fields) {
      const hasEnhancedFields = Object.entries(group.collect_info_fields).some(
        ([key, value]) => value === true && !STANDARD_INFO_FIELDS.has(key)
      );
      if (hasEnhancedFields) return false;
    }

    if (group.info_required) return false;

    return true;
  });
}
